import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikProps } from 'formik';
import { LoadingOutlined } from '@ant-design/icons';

import { I18nNameSpaces } from 'common/services/i18n';
import { ClientVerificationFormEmailValues } from 'common/models/Forms';
import { InputSearchField } from 'common/fields';
import { ResultMessage } from 'common/components';
import { makeEmailSchema, makeCodeSchema } from './schema';

interface Props {
  success?: string | null;
  error?: string | null;
  loadingRequest?: boolean;
  loadingVerify?: boolean;
  initialValues?: Partial<ClientVerificationFormEmailValues>;
  disableVerify?: boolean;

  onRequestCode: (email: string) => void;
  onVerifyCode: (code: string) => void;
}

export default function ClientVerificationForm({
  success,
  error,
  loadingRequest,
  loadingVerify,
  initialValues,
  disableVerify,

  onRequestCode,
  onVerifyCode,
}: Props) {
  const { t } = useTranslation(I18nNameSpaces.Forms);

  const emailSchema = makeEmailSchema(t);
  const codeSchema = makeCodeSchema(t);

  function renderCodeRequestForm({ submitForm }: FormikProps<Pick<ClientVerificationFormEmailValues, 'email'>>) {
    return (
      <Form>
        <InputSearchField
          name="email"
          label={t('clientForm.email.label')}
          defaultValue={initialValues?.email ?? ''}
          placeholder={t('clientForm.email.placeholder')}
          enterButton={loadingRequest ? <LoadingOutlined /> : t('verificationForm.email.request.submit')}
          onSearch={submitForm}
          disabled={loadingRequest}
        />
      </Form>
    );
  }

  function renderCodeVerifyForm({ submitForm }: FormikProps<Pick<ClientVerificationFormEmailValues, 'code'>>) {
    return (
      <Form>
        <InputSearchField
          name="code"
          label={t('verificationForm.email.code.label')}
          placeholder={t('verificationForm.email.code.placeholder')}
          autoComplete="off"
          enterButton={loadingVerify ? <LoadingOutlined /> : t('verificationForm.email.verify.submit')}
          onSearch={submitForm}
          disabled={loadingVerify || disableVerify}
        />
      </Form>
    );
  }

  return (
    <div className="client-verification-form">
      <Formik
        initialValues={{
          email: initialValues?.email ?? '',
        }}
        onSubmit={({ email }) => {
          onRequestCode(email);
        }}
        validationSchema={emailSchema}
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize
      >
        {renderCodeRequestForm}
      </Formik>
      <Formik
        initialValues={{
          code: '',
        }}
        onSubmit={({ code }) => {
          onVerifyCode(code);
        }}
        validationSchema={codeSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {renderCodeVerifyForm}
      </Formik>
      {Boolean(error) && <ResultMessage type="error">{error}</ResultMessage>}
      {Boolean(success) && !error && <ResultMessage type="success">{success}</ResultMessage>}
    </div>
  );
}
